import {
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  styled,
} from '@mui/material'
import { HBButton } from '../Card/HBButton'
import { CloseModalIcon } from './Icons'

interface ModalProps {
  open: boolean
  closeModal: () => void
  title: React.ReactNode
  image?: JSX.Element
  action?: () => void
  actionText?: React.ReactNode
  cancel?: boolean
  cancelText?: React.ReactNode
  children?: React.ReactNode
}

interface ButtonProps {
  cancel?: boolean
}

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    maxWidth: '654px',
    margin: '0.75rem',
    [theme.breakpoints.up('sm')]: {
      margin: '2rem',
    },
  },
}))

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '1rem',
  overflowX: 'hidden',
  [theme.breakpoints.up('sm')]: {
    padding: '1.75rem',
  },
}))

const InformationContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2rem',
})

const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '1rem',
  [theme.breakpoints.up('sm')]: {
    gap: '1.5rem',
  },
}))

const ModalTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  fontWeight: 500,
  fontSize: '1.25rem',
  padding: '0 1.5rem',
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.5rem',
    padding: '0 3rem',
  },
}))

const ModalButton = styled(HBButton, {
  shouldForwardProp: (propName) => propName !== 'cancel',
})<ButtonProps>(({ cancel, theme }) => ({
  width: 'fit-content',
  fontSize: '0.75rem',
  '&:hover': {
    boxShadow: '0 0 0 0',
    backgroundColor: '#BF4F49',
  },
  ...(cancel && {
    color: 'black',
    backgroundColor: 'white',
    border: '1px solid #595959',
    '&:hover': {
      backgroundColor: '#e2e2e2',
      boxShadow: '0 0 0 0',
    },
  }),
  [theme.breakpoints.up('sm')]: {
    fontSize: '1rem',
  },
}))

const ModalIconButton = styled(IconButton)({
  ' .MuiSvgIcon-root': {
    width: 18,
    height: 'auto',
  },
  '&:focus, &:focus-within': {
    backgroundColor: 'rgba(0, 0, 0, 0.06)',
    outline: '2px solid #1270B8',
    boxShadow: '0 0 0 0',
  },
})

export const HeadBoxModal = ({
  open,
  closeModal,
  title,
  image,
  children,
  cancel,
  cancelText,
  action,
  actionText,
}: ModalProps): JSX.Element => {
  return (
    <StyledDialog open={open} onClose={closeModal}>
      <StyledDialogContent>
        <DialogActions>
          <ModalIconButton onClick={closeModal}>
            <CloseModalIcon titleAccess='Close modal' />
          </ModalIconButton>
        </DialogActions>
        <InformationContainer>
          {image && image}
          <ModalTitle variant='h2'>{title}</ModalTitle>
          {children && children}
          {(cancel ?? action) && (
            <ButtonContainer>
              {cancel && (
                <ModalButton
                  cancel
                  disableRipple
                  variant='contained'
                  onClick={() => closeModal()}
                >
                  {cancelText}
                </ModalButton>
              )}
              {action && (
                <ModalButton disableRipple variant='contained' onClick={action}>
                  {actionText}
                </ModalButton>
              )}
            </ButtonContainer>
          )}
        </InformationContainer>
      </StyledDialogContent>
    </StyledDialog>
  )
}
